import {
  type JobProgressionFlow,
  type JobProgressionStep,
  type JobProgressionStepStatus,
  type OverrideableJobProgressionStepStatus,
} from '@/features/jobs/taxes/progression/JobProgression'
import { match } from '@/utils/pattern-match-util'

/**
 * The order steps are listed in, whatever the flow.
 */
export const flowStepOrder: JobProgressionStepStatus[] = [
  'NotStarted',
  'ExtensionNotStarted',
  'JobStarted',
  'OpenItems',
  'DraftInReview',
  'DraftRejected',
  'DraftApproved',
  'AuthorizationRequested',
  'AuthorizationSigned',
  'ReturnFiled',
  'DeliverableCompleted',
  'PreparationCompleted',
  'ReviewCompleted',
  'ExtensionInProgress',
  'ExtensionConfirmed',
  'ExtensionAccepted',
  'ExtensionRejected',
  'ProgressionWaitingOnClient',
  'ReadyToFile',
]

/**
 * Steps listed only while they are the current step.
 */
export const stepsShownWhenCurrent: JobProgressionStepStatus[] = [
  'OpenItems',
  'DraftRejected',
]

/**
 * Steps listed only once the job has reached them.
 */
export const stepsShownWhenReached: JobProgressionStepStatus[] = [
  'AuthorizationRequested',
  'ExtensionRejected',
]

/**
 * Tracks the job progression completion at a step
 */
interface ProgressionCompletion {
  completed: number
  pending: number
}

/**
 * What a progression step shows the pro and the client.
 */
export interface ProgressionStepDetails {
  title: string
  proDescription: string
  clientDescription: string
  progress: ProgressionCompletion
}

/**
 * The details of every step of a flow; `undefined` for steps the flow does not
 * have.
 */
type ProgressionStepsDetails = Record<
  JobProgressionStepStatus,
  ProgressionStepDetails | undefined
>

const reviewProgressionStepsDetails: ProgressionStepsDetails = {
  NotStarted: {
    title: 'Not started',
    proDescription:
      'Confirm when you have started working on this job. If there are any open items, report them in the next step.',
    clientDescription:
      'Please ensure that all necessary documents are ready. Your Pro will reach out if there are any open items.',
    progress: { completed: 0, pending: 20 },
  },
  JobStarted: {
    title: 'Job in progress',
    proDescription:
      "If anything is missing to complete the job, please report open items.\n\u200B\nTake a moment to look over the prep notes left by the tax preparer.\n\u200B\nOnce you've completed the deliverable, request draft approval from your client.",
    clientDescription:
      'Your return is being reviewed. When ready, your Pro will contact you to review the draft return.',
    progress: { completed: 20, pending: 20 },
  },

  OpenItems: {
    title: 'Open items',
    proDescription:
      'Contact the client and share a missing items list. Once you receive all the items, continue reviewing.',
    clientDescription:
      'There are outstanding documents. Your Pro will share a missing items list to review. Please notify your Pro once the documents are ready.',
    progress: { completed: 20, pending: 20 },
  },
  DraftInReview: {
    title: 'Draft review',
    proDescription: 'Please wait for your client to review your deliverable.',
    clientDescription:
      'Your Pro has finalized a draft return for review. Please review and notify your Pro if any changes are necessary.',
    progress: { completed: 40, pending: 20 },
  },
  DraftRejected: {
    title: 'Changes requested',
    proDescription:
      "Once you're done making the required changes, request draft approval from your client. If anything is missing to complete job, please report open items.",
    clientDescription:
      'You requested changes to the return. Once revised, your Pro will request another draft approval from you.',
    progress: { completed: 20, pending: 20 },
  },
  DraftApproved: {
    title: 'Draft Approved',
    proDescription:
      'The draft has been approved. Please upload any review notes for the preparation pro.',
    clientDescription:
      'You approved the draft. Your Pro will check back in to finalize your return and complete the job.',
    progress: { completed: 60, pending: 20 },
  },
  ReviewCompleted: {
    title: 'Review Completed',
    proDescription:
      'After uploading your review notes, please complete the job.',
    clientDescription:
      'You approved the draft. Your Pro will check back in to finalize your return and complete the job.',
    progress: { completed: 80, pending: 20 },
  },
  PreparationCompleted: undefined,
  Unspecified: undefined,
  AuthorizationRequested: undefined,
  AuthorizationSigned: undefined,
  ReturnFiled: undefined,
  DeliverableCompleted: undefined,
  ExtensionNotStarted: undefined,
  ExtensionInProgress: undefined,
  ExtensionConfirmed: undefined,
  ExtensionAccepted: undefined,
  ExtensionRejected: undefined,
  ReadyToFile: undefined,
  ProgressionWaitingOnClient: undefined,
}

const preparationProgressionStepsDetails: ProgressionStepsDetails = {
  NotStarted: {
    title: 'Not started',
    proDescription:
      'Confirm when you have started working on this job. If there are any open items, report them in the next step.',
    clientDescription:
      'Please ensure that all necessary documents are ready. Your Pro will reach out if there are any open items.',
    progress: { completed: 0, pending: 33 },
  },
  JobStarted: {
    title: 'Job in progress',
    proDescription:
      "If anything is missing to complete the job, please report open items.\n\u200B\nOnce you've finished the preparation, please upload the notes to the job.",
    clientDescription:
      'Your return is being prepared. Once your Pro has finished, they will upload their preparation notes.',
    progress: { completed: 40, pending: 40 },
  },

  OpenItems: {
    title: 'Open items',
    proDescription:
      'Contact the client and share a missing items list. Once you receive all the items, continue the preparation.',
    clientDescription:
      'There are outstanding documents. Your Pro will share a missing items list to review. Please notify your Pro once the documents are ready.',
    progress: { completed: 0, pending: 40 },
  },
  PreparationCompleted: {
    title: 'Preparation complete',
    proDescription:
      'Congratulations! All tasks are now complete.\n\u200B\nOnce the review is finished, the reviewer’s notes will be uploaded to the job.',
    clientDescription:
      'Your Pro has completed the preparation for your tax return. The preparation notes can be found in the Documents section.',
    progress: { completed: 80, pending: 20 },
  },

  DraftInReview: undefined,
  DraftRejected: undefined,
  DraftApproved: undefined,
  Unspecified: undefined,
  AuthorizationRequested: undefined,
  AuthorizationSigned: undefined,
  ReturnFiled: undefined,
  DeliverableCompleted: undefined,
  ReviewCompleted: undefined,
  ExtensionNotStarted: undefined,
  ExtensionInProgress: undefined,
  ExtensionConfirmed: undefined,
  ExtensionAccepted: undefined,
  ExtensionRejected: undefined,
  ReadyToFile: undefined,
  ProgressionWaitingOnClient: undefined,
}

const outsourcingProgressionStepsDetails: ProgressionStepsDetails = {
  NotStarted: {
    title: 'Not started',
    proDescription:
      'Confirm when you have started working on this job. If there are any open items, report them in the next step.',
    clientDescription:
      'Please ensure that all necessary documents are ready. Your Pro will reach out if there are any open items.',
    progress: { completed: 0, pending: 20 },
  },
  OpenItems: {
    title: 'Open items',
    proDescription:
      'Contact the client and share a missing items list. Once you receive all the items, continue preparation.',
    clientDescription:
      'There are outstanding documents. Your Pro will share a missing items list to review. Please notify your Pro once the documents are ready.',
    progress: { completed: 0, pending: 20 },
  },
  JobStarted: {
    title: 'Job in progress',
    proDescription:
      "If anything is missing to complete the job, please report open items.\n\u200B\nOnce you've completed the deliverable, request draft approval from your client.",
    clientDescription:
      'A draft return is being prepared. When ready, your Pro will contact you to review.',
    progress: { completed: 33, pending: 20 },
  },
  DraftInReview: {
    title: 'Draft review',
    proDescription: 'Please wait for your client to review your deliverable.',
    clientDescription:
      'Your Pro has finalized a draft return for review. Please review and notify your Pro if any changes are necessary.',
    progress: { completed: 66, pending: 20 },
  },
  DraftRejected: {
    title: 'Changes requested',
    proDescription:
      "Once you're done making the required changes, request draft approval from your client. If anything is missing to complete job, please report open items.",
    clientDescription:
      'You requested changes to the return. Once revised, your Pro will request another draft approval from you.',
    progress: { completed: 50, pending: 20 },
  },
  DraftApproved: {
    title: 'Draft approved',
    proDescription:
      'The client approved the deliverable. \n\u200B\n Check back in with the client to finalize the return and complete the job.',
    clientDescription:
      'You approved the draft. Your Pro will check back in to finalize your return and complete the job.',
    progress: { completed: 80, pending: 20 },
  },
  Unspecified: undefined,
  AuthorizationRequested: undefined,
  AuthorizationSigned: undefined,
  ReturnFiled: undefined,
  DeliverableCompleted: undefined,
  PreparationCompleted: undefined,
  ReviewCompleted: undefined,
  ExtensionNotStarted: undefined,
  ExtensionInProgress: undefined,
  ExtensionConfirmed: undefined,
  ExtensionAccepted: undefined,
  ExtensionRejected: undefined,
  ReadyToFile: undefined,
  ProgressionWaitingOnClient: undefined,
}

const retailProgressionStepsDetails: ProgressionStepsDetails = {
  NotStarted: {
    title: 'Not started',
    proDescription:
      'Confirm when you have started working on this job. If there are any open items, report them in the next step.',
    clientDescription:
      'Please ensure that all necessary documents are ready. Your Pro will reach out if there are any open items.',
    progress: { completed: 0, pending: 20 },
  },
  OpenItems: {
    title: 'Open items',
    proDescription:
      'Contact the client and share a missing items list. Once you receive all the items, continue preparation.',
    clientDescription:
      'There are outstanding documents. Your Pro will share a missing items list to review. Please notify your Pro once the documents are ready.',
    progress: { completed: 0, pending: 20 },
  },
  JobStarted: {
    title: 'Job in progress',
    proDescription:
      "If anything is missing to complete the job, please report open items. \n\u200B\nOnce you've completed the deliverable, request draft approval from your client.",
    clientDescription:
      'A draft return is being prepared. When ready, your Pro will contact you to review.',
    progress: { completed: 20, pending: 20 },
  },
  DraftInReview: {
    title: 'Draft review',
    proDescription: 'Please wait for your client to review your deliverable.',
    clientDescription:
      'Your Pro has a draft ready for review. Please review and notify your Pro if any changes are necessary.',
    progress: { completed: 40, pending: 20 },
  },
  DraftRejected: {
    title: 'Changes requested',
    proDescription:
      "Once you're done making the required changes, request draft approval from your client. If anything is missing to complete job, please report open items.",
    clientDescription:
      'You requested changes to the return. Once revised, your Pro will request another draft approval from you.',
    progress: { completed: 40, pending: 20 },
  },
  DraftApproved: {
    title: 'Draft approved',
    proDescription:
      'Once you have signed and uploaded **Form 8879**, proceed to the next step.',
    clientDescription:
      'You approved the draft. Your Pro will check back in to finalize your return and complete the job.',
    progress: { completed: 50, pending: 20 },
  },
  AuthorizationRequested: {
    title: 'Authorization requested',
    proDescription:
      'Contact the client and share **Form 8879**. Once e-file authorization is complete, move onto the next step.',
    clientDescription: '',
    progress: { completed: 60, pending: 20 },
  },
  AuthorizationSigned: {
    title: 'Authorization received',
    proDescription:
      'File the return, and upload both the final return and e-file confirmation to proceed.',
    clientDescription: '',
    progress: { completed: 70, pending: 20 },
  },
  ReturnFiled: {
    title: 'Return filed',
    proDescription:
      'You have filed the return. Check back in with the client to finalize and complete the job.',
    clientDescription: '',
    progress: { completed: 80, pending: 20 },
  },
  DeliverableCompleted: undefined,
  Unspecified: undefined,
  PreparationCompleted: undefined,
  ReviewCompleted: undefined,
  ExtensionNotStarted: undefined,
  ExtensionInProgress: undefined,
  ExtensionConfirmed: undefined,
  ExtensionAccepted: undefined,
  ExtensionRejected: undefined,
  ReadyToFile: undefined,
  ProgressionWaitingOnClient: undefined,
}

const essentialProgressionStepsDetails: ProgressionStepsDetails = {
  NotStarted: {
    title: 'Not started',
    proDescription: 'Confirm when you have started working on this job.',
    clientDescription:
      'A Pro will begin working on the job shortly. At this stage, we can no longer accept any documents.',
    progress: { completed: 0, pending: 33 },
  },
  JobStarted: {
    title: 'Job in progress',
    proDescription:
      "Once you've completed the deliverable, upload the annotated source documents. If applicable, upload a missing items list.",
    clientDescription: 'The deliverable is being prepared.',
    progress: { completed: 33, pending: 33 },
  },
  DeliverableCompleted: {
    title: 'Job completed',
    proDescription:
      "You've successfully uploaded the deliverable. Complete the job to proceed.",
    clientDescription:
      'Your Pro has completed the work and will close the job soon.',
    progress: { completed: 67, pending: 33 },
  },
  DraftInReview: undefined,
  DraftRejected: undefined,
  DraftApproved: undefined,
  AuthorizationRequested: undefined,
  AuthorizationSigned: undefined,
  OpenItems: undefined,
  Unspecified: undefined,
  ReturnFiled: undefined,
  PreparationCompleted: undefined,
  ReviewCompleted: undefined,
  ExtensionNotStarted: undefined,
  ExtensionInProgress: undefined,
  ExtensionConfirmed: undefined,
  ExtensionAccepted: undefined,
  ExtensionRejected: undefined,
  ReadyToFile: undefined,
  ProgressionWaitingOnClient: undefined,
}

const personalExtensionProgressionStepsDetails: ProgressionStepsDetails = {
  ExtensionNotStarted: {
    title: 'Not started',
    proDescription: 'Please confirm when you have begun working on this job.',
    clientDescription:
      'Please ensure that all necessary documents are prepared. Your pro will contact you if any additional information is needed.',
    progress: { completed: 0, pending: 20 },
  },
  ExtensionInProgress: {
    title: 'Extension in progress',
    proDescription:
      'After filing the extension, please upload the e-file confirmation.',
    clientDescription:
      'Your extension is being prepared. Once your Pro has finished, they will upload the e-file confirmation.',
    progress: { completed: 20, pending: 20 },
  },
  ExtensionConfirmed: {
    title: 'Extension status',
    proDescription:
      'Notify the client once the IRS responds to the extension request. Let them know whether the extension has been accepted or rejected.',
    clientDescription:
      'Your pro has filed an extension on your behalf. You will receive an update when the IRS has either accepted or rejected the extension.',
    progress: { completed: 40, pending: 20 },
  },
  ExtensionAccepted: {
    title: 'Extension accepted',
    proDescription:
      'The extension has been accepted by the IRS. You may complete the job.',
    clientDescription: 'The extension was successfully accepted by the IRS.',
    progress: { completed: 80, pending: 20 },
  },
  ExtensionRejected: {
    title: 'Extension rejected',
    proDescription:
      'The extension request was rejected. Please upload the rejection notice from the IRS. After uploading, you may complete the job.',
    clientDescription:
      'The extension was rejected by the IRS. This could be due to an already filed extension on your behalf. Your pro can answer any questions you might have.',
    progress: { completed: 80, pending: 20 },
  },
  ProgressionWaitingOnClient: {
    title: 'Waiting on client',
    proDescription:
      'Please wait on the client to notify you when they’re ready to continue filing.',
    clientDescription:
      'Please notify your pro when you’re ready to resume your tax return.',
    progress: { completed: 80, pending: 20 },
  },
  NotStarted: undefined,
  OpenItems: undefined,
  JobStarted: undefined,
  DraftInReview: undefined,
  DraftRejected: undefined,
  DraftApproved: undefined,
  AuthorizationRequested: undefined,
  AuthorizationSigned: undefined,
  ReturnFiled: undefined,
  DeliverableCompleted: undefined,
  Unspecified: undefined,
  PreparationCompleted: undefined,
  ReviewCompleted: undefined,
  ReadyToFile: undefined,
}

const businessExtensionProgressionStepsDetails: ProgressionStepsDetails = {
  ExtensionNotStarted: {
    title: 'Not started',
    proDescription: 'Please confirm when you have begun working on this job.',
    clientDescription:
      'Please ensure that all necessary documents are prepared. Your pro will contact you if any additional information is needed.',
    progress: { completed: 0, pending: 20 },
  },
  ExtensionInProgress: {
    title: 'Extension in progress',
    proDescription:
      'After filing the extension, please upload the e-file confirmation.',
    clientDescription:
      'Your extension is being prepared. Once your Pro has finished, they will upload the e-file confirmation.',
    progress: { completed: 20, pending: 20 },
  },
  ExtensionConfirmed: {
    title: 'Extension status',
    proDescription:
      'Notify the client once the IRS responds to the extension request. Let them know whether the extension has been accepted or rejected.',
    clientDescription:
      'Your pro has filed an extension on your behalf. You will receive an update when the IRS has either accepted or rejected the extension.',
    progress: { completed: 40, pending: 20 },
  },
  ExtensionAccepted: {
    title: 'Extension accepted',
    proDescription:
      'The extension has been accepted by the IRS. You may complete the job.',
    clientDescription: 'The extension was successfully accepted by the IRS.',
    progress: { completed: 80, pending: 20 },
  },
  ExtensionRejected: {
    title: 'Extension rejected',
    proDescription:
      'The extension request was rejected. Please upload the rejection notice from the IRS. After uploading, you may complete the job.',
    clientDescription:
      'The extension was rejected by the IRS. This could be due to an already filed extension on your behalf. Your pro can answer any questions you might have.',
    progress: { completed: 80, pending: 20 },
  },
  ProgressionWaitingOnClient: {
    title: 'Waiting on client',
    proDescription:
      'Please wait on the client to notify you when they’re ready to continue filing.',
    clientDescription:
      'Please notify your pro when you’re ready to resume your tax return.',
    progress: { completed: 80, pending: 20 },
  },
  NotStarted: undefined,
  OpenItems: undefined,
  JobStarted: undefined,
  DraftInReview: undefined,
  DraftRejected: undefined,
  DraftApproved: undefined,
  AuthorizationRequested: undefined,
  AuthorizationSigned: undefined,
  ReturnFiled: undefined,
  DeliverableCompleted: undefined,
  Unspecified: undefined,
  PreparationCompleted: undefined,
  ReviewCompleted: undefined,
  ReadyToFile: undefined,
}

const outsourcingExtensionProgressionStepsDetails: ProgressionStepsDetails = {
  ExtensionNotStarted: {
    title: 'Not started',
    proDescription: 'Please confirm when you have begun working on this job.',
    clientDescription:
      'Please ensure that all necessary documents are prepared. Your pro will contact you if any additional information is needed.',
    progress: { completed: 0, pending: 20 },
  },
  ProgressionWaitingOnClient: {
    title: 'Waiting on client',
    proDescription:
      'Please wait on the client to notify you when they’re ready to continue.',
    clientDescription: 'Please notify your pro when you’re ready to continue.',
    progress: { completed: 80, pending: 20 },
  },
  ReadyToFile: {
    title: 'Extension completed',
    proDescription:
      'The extension has been completed. You may complete the job.',
    clientDescription: 'The extension was successfully completed by your Pro.',
    progress: { completed: 100, pending: 0 },
  },
  NotStarted: undefined,
  OpenItems: undefined,
  JobStarted: undefined,
  DraftInReview: undefined,
  DraftRejected: undefined,
  DraftApproved: undefined,
  AuthorizationRequested: undefined,
  AuthorizationSigned: undefined,
  ReturnFiled: undefined,
  DeliverableCompleted: undefined,
  Unspecified: undefined,
  PreparationCompleted: undefined,
  ReviewCompleted: undefined,
  ExtensionInProgress: undefined,
  ExtensionConfirmed: undefined,
  ExtensionAccepted: undefined,
  ExtensionRejected: undefined,
}

/**
 * Fallback titles for steps whose status has no entry in the active
 * flow's details table (e.g. historical steps from another flow).
 */
const genericProgressionStepTitles: Record<JobProgressionStepStatus, string> = {
  Unspecified: 'Progression step',
  JobStarted: 'Job in progress',
  OpenItems: 'Open items',
  DraftInReview: 'Draft review',
  DraftRejected: 'Changes requested',
  DraftApproved: 'Draft approved',
  AuthorizationRequested: 'Authorization requested',
  AuthorizationSigned: 'Authorization received',
  ReturnFiled: 'Return filed',
  NotStarted: 'Not started',
  DeliverableCompleted: 'Deliverable completed',
  PreparationCompleted: 'Preparation complete',
  ReviewCompleted: 'Review completed',
  ExtensionNotStarted: 'Not started',
  ExtensionInProgress: 'Extension in progress',
  ExtensionConfirmed: 'Extension confirmed',
  ExtensionAccepted: 'Extension accepted',
  ExtensionRejected: 'Extension rejected',
  ProgressionWaitingOnClient: 'Waiting on client',
  ReadyToFile: 'Ready to file',
}

/**
 * Resolves the display title for a progression step from the flow's
 * details table, falling back to a generic title.
 */
export function getProgressionStepTitle(
  flow: JobProgressionFlow,
  status: JobProgressionStepStatus,
): string {
  return (
    getFlowStepsDetails(flow)[status]?.title ??
    genericProgressionStepTitles[status]
  )
}

function getExtensionStepDetails(
  stepDetails: ProgressionStepsDetails,
  currentStep: JobProgressionStepStatus,
  progressionSteps: JobProgressionStep[] | undefined,
) {
  if (currentStep === 'ProgressionWaitingOnClient') {
    const title = progressionSteps?.some(
      (p) => p.status === 'ExtensionAccepted',
    )
      ? 'Extension accepted'
      : progressionSteps?.some((p) => p.status === 'ExtensionRejected')
        ? 'Extension rejected'
        : 'Waiting on client'

    return {
      ...stepDetails.ProgressionWaitingOnClient,
      title,
    }
  }

  return stepDetails[currentStep]
}

/**
 * The details of the current step of a flow. Extension flows title the
 * waiting-on-client step after how the extension was answered.
 *
 * @param flow
 * @param status
 * @param steps The job's progression steps so far.
 */
export function getProgressionStepDetails(
  flow: JobProgressionFlow,
  status: JobProgressionStepStatus,
  steps: JobProgressionStep[] | undefined,
) {
  return match(flow, {
    Outsourcing: () => outsourcingProgressionStepsDetails[status],
    Retail: () => retailProgressionStepsDetails[status],
    Essential: () => essentialProgressionStepsDetails[status],
    Preparation: () => preparationProgressionStepsDetails[status],
    Review: () => reviewProgressionStepsDetails[status],
    PersonalExtension: () =>
      getExtensionStepDetails(
        personalExtensionProgressionStepsDetails,
        status,
        steps,
      ),
    BusinessExtension: () =>
      getExtensionStepDetails(
        businessExtensionProgressionStepsDetails,
        status,
        steps,
      ),
    OutsourcingExtension: () =>
      outsourcingExtensionProgressionStepsDetails[status],
    None: () => undefined,
  })
}

/**
 * The step details for every status in a flow.
 *
 * @param flow
 */
export function getFlowStepsDetails(
  flow: JobProgressionFlow,
): Partial<ProgressionStepsDetails> {
  return match(flow, {
    Outsourcing: () => outsourcingProgressionStepsDetails,
    Retail: () => retailProgressionStepsDetails,
    Essential: () => essentialProgressionStepsDetails,
    Preparation: () => preparationProgressionStepsDetails,
    Review: () => reviewProgressionStepsDetails,
    PersonalExtension: () => personalExtensionProgressionStepsDetails,
    BusinessExtension: () => businessExtensionProgressionStepsDetails,
    OutsourcingExtension: () => outsourcingExtensionProgressionStepsDetails,
    None: () => ({}),
  })
}

/**
 * The steps an admin can move a job back or forward to from its current step.
 *
 * @param flow
 * @param status
 */
export function getOverrideableProgressionSteps(
  flow: JobProgressionFlow,
  status: JobProgressionStepStatus,
): [OverrideableJobProgressionStepStatus, ProgressionStepDetails][] {
  return match(flow, {
    Retail: () => {
      if (status === 'DraftInReview') {
        return [
          ['ReturnFiled', retailProgressionStepsDetails.ReturnFiled!],
          ['JobStarted', retailProgressionStepsDetails.JobStarted!],
          ['OpenItems', retailProgressionStepsDetails.OpenItems!],
        ]
      }
      return [['ReturnFiled', retailProgressionStepsDetails.ReturnFiled!]]
    },
    Outsourcing: () => {
      if (status === 'DraftInReview') {
        return [
          ['JobStarted', retailProgressionStepsDetails.JobStarted!],
          ['OpenItems', retailProgressionStepsDetails.OpenItems!],
        ]
      }
      return []
    },
    Essential: () => [],
    None: () => [],
    Preparation: () => [],
    Review: () => {
      if (status === 'DraftInReview') {
        return [
          ['JobStarted', retailProgressionStepsDetails.JobStarted!],
          ['OpenItems', retailProgressionStepsDetails.OpenItems!],
        ]
      }
      return []
    },
    PersonalExtension: () => [],
    BusinessExtension: () => [],
    OutsourcingExtension: () => [],
  })
}
